import React from 'react';
import Link from 'next/link';
import round from 'lodash/round';
import GeneralChart from '../GeneralChart';
import { CusEcharts } from '@/components/AsyncComponent';
import InfoTooltip from '../InfoTooltip';
import type { ECOption } from '@/components/CusEcharts';
import { useClintMediaQuery } from '@/utils/hooks/useClintMediaQuery';
import { CityContext } from '@/context/create-context';
import { JOB_RANKING_PAGE_ROUTER_PATH } from '@/constants/router-path';
import { TAB_INDUSTRY_SALARY } from '@/constants/city';
import { trackClickEvent } from '@/utils/site-track';

const DEFAULT_COUNT = 9;

const IndustrySalary = () => {
  const chartRef = React.useRef(null);
  const isMobile = useClintMediaQuery();
  const {
    pinyin: cityPinyin,
    name: cityName,
    industry_salary: industrySalary,
  } = React.useContext(CityContext);

  const config = React.useMemo<ECOption>(() => {
    const chartData = industrySalary.slice(0, DEFAULT_COUNT);
    const fillChartData = chartData.concat(
      new Array(DEFAULT_COUNT)
        .fill({ name: '', avg: undefined })
        .slice(industrySalary.length),
    );
    const chartName = chartData.map((item) => item.name);
    const fillChartName = fillChartData.map((item) => item.name);
    const fillChartSalary = fillChartData.map((item) => round(item.avg, -2));

    const seoInfo = `截止至当前数据统计：在${cityName}招聘市场中薪资最高的行业为${
      chartName[0]
    }；${chartName[1]}行业平均薪资位居第二。`;

    return {
      grid: {
        show: false,
        tooltip: { show: false },
        left: 24,
        right: 24,
        top: 20,
        bottom: 30,
      },
      xAxis: {
        type: 'category',
        data: fillChartName,
        show: false,
        boundaryGap: false,
      },
      yAxis: {
        type: 'value',
        show: false,
      },
      series: [
        {
          type: 'bar',
          cursor: 'default',
          barWidth: isMobile ? 20 : 40,
          data: fillChartSalary,
          itemStyle: {
            borderRadius: isMobile ? 4 : 8,
            color: ({ dataIndex }) =>
              dataIndex === 0
                ? 'rgba(63, 115, 211)'
                : 'rgba(63, 115, 211, 0.5)',
          },
          label: {
            show: true,
            position: 'top',
            fontSize: 14,
            color: '#2d2d2d',
            formatter: ({ value }) => '¥' + value,
          },
        },
      ],
      seoInfo,
    };
  }, [isMobile, industrySalary]);

  return (
    <GeneralChart
      title="行业平均月薪 Top 10"
      subTip="洞察哪些行业回报最高，为您的行业选择提供薪酬参考。"
    >
      <div className="relative px-2">
        <div className="w-full relative h-[250px] min-w-[640px]">
          <CusEcharts chartRef={chartRef} config={config} />
          <div className="absolute bottom-0 left-0 right-0 h-[22px] px-1 text-sm flex justify-between items-center">
            <>
              {industrySalary.map(({ name, id }, index) => (
                <Link
                  className="inline-block h-full relative w-10 hover:text-web-blue-500"
                  key={id + index}
                  href={`${JOB_RANKING_PAGE_ROUTER_PATH}${cityPinyin}_${id}/`}
                  title={name}
                  data-ele-group={`${TAB_INDUSTRY_SALARY}_industry`}
                  data-ele-id={`industry_${name}`}
                  onClick={trackClickEvent}
                >
                  <span className="underline absolute top-0 left-1/2 whitespace-nowrap -translate-x-1/2">
                    {name}
                  </span>
                </Link>
              ))}
              {new Array(DEFAULT_COUNT - industrySalary.length)
                .fill('')
                .map((item, index) => (
                  <div className="h-full w-10" key={item + index} />
                ))}
            </>
          </div>
        </div>
      </div>
      <p className="flex mt-4">
        <InfoTooltip className="mt-[5px] mr-1 shrink-0" />
        {config.seoInfo}
      </p>
    </GeneralChart>
  );
};

export default IndustrySalary;
